import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { LocalizedText, lt } from '@mews/framework';
import { Banner, Button, Stack, Typography } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { TextKey, DataTestId } from '@mews-commander/enums';
import { CompaniesFormValues, companiesSchema } from '../../../../../schemas';
import { getSelectedCompaniesCount } from '../../../../../utils/companies';
import { RoutingRulesFormFields } from '../../../utils';
import { CompaniesList } from './CompaniesList';

interface CompaniesDialogProps {
    active: boolean;
    onClose: () => void;
}

export const CompaniesDialog = ({ active, onClose }: CompaniesDialogProps) => {
    const [initialSelectedCompanies, setInitialSelectedCompanies] = useState<CompaniesFormValues>({});
    const { watch, setValue } = useFormContext();
    const [isNoCompanySelected, setIsNoCompanySelected] = useState(false);
    const selectedCompanies: CompaniesFormValues = watch(RoutingRulesFormFields.companies());

    const handleDialogClose = () => {
        setValue(RoutingRulesFormFields.companies(), initialSelectedCompanies);
        if (getSelectedCompaniesCount(initialSelectedCompanies) === 0) {
            setValue(RoutingRulesFormFields.companiesEnabled(), false);
        }
        setIsNoCompanySelected(false);
        onClose();
    };

    const handleDialogSave = () => {
        try {
            companiesSchema.parse(selectedCompanies);
            setIsNoCompanySelected(false);
            onClose();
        } catch (error) {
            setIsNoCompanySelected(true);
        }
    };

    useEffect(() => {
        if (active) {
            setInitialSelectedCompanies(watch(RoutingRulesFormFields.companies()));
        }
    }, [watch, active]);

    const footer = {
        primary: (
            <Button
                variant="primary"
                type="submit"
                size="medium"
                onClick={handleDialogSave}
                data-test-id={DataTestId.RoutingRulesCompaniesDialogSave}
            >
                {lt(TextKey.Save)}
            </Button>
        ),
        secondary: (
            <Button
                variant="tertiary"
                type="button"
                size="medium"
                onClick={handleDialogClose}
                data-test-id={DataTestId.RoutingRulesCompaniesDialogCancel}
            >
                {lt(TextKey.Cancel)}
            </Button>
        ),
    };

    const contentRef = useRef<HTMLDivElement>(null);

    return (
        <FloatingPanel
            active={active}
            onClose={handleDialogClose}
            title={lt(TextKey.RoutingRulesFormCompaniesDialogTitle)}
            footer={footer}
            contentRef={contentRef}
        >
            <Stack vertical spacing="200">
                <Typography variant="secondary" textStyle="bodyMedium" as="div">
                    <LocalizedText textKey={TextKey.RoutingRulesFormCompaniesDialogDescription} />
                </Typography>
                {isNoCompanySelected && (
                    <Banner
                        variant="error"
                        primaryText={
                            <LocalizedText textKey={TextKey.RoutingRulesFormCompaniesDialogValidationMessage} />
                        }
                    />
                )}
                <CompaniesList contentRef={contentRef} />
            </Stack>
        </FloatingPanel>
    );
};
